import React, { useCallback, useEffect, useState } from "react";
import { CloudUpload, Edit2, X, Trash2, Plus, Info } from "lucide-react";
import UploadBtn, { UploadStatus } from "./uploadSurplus";
import PowerAddOffer from "./PowerAddOffer";
import { useOptimizerNavigation } from "../utility/useOptimizerNavigation";
import {
  getStatusTraderOffersData,
  StartSimulationProcurementData,
  AddSurplusDeficitData,
} from "../../../../Rest_api/DemoRestApi";
import TimeInput from "../../../../components/TimeInput/TimeInput";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import Loading from "../../../../components/Loading";

function SurplusDeficitForm({
  refreshKey,
  setRefreshKey,
}: {
  refreshKey: any;
  setRefreshKey: any;
}) {
  const { navigateToPage } = useOptimizerNavigation();
  const [surplusDeficitUploaded, setSurplusDeficitUploaded] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("pending");
  const [tradeAdded, setTradeAdded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any[] | null>(null);

  const handleDataParsed = (data: any[], name: string) => {
    setParsedData(data);
    setFileName(name);
    setSurplusDeficitUploaded(true);
    setUploadStatus("pending"); // Reset so it can be re-submitted
  };

  const handleFileDeleted = () => {
    setParsedData(null);
    setFileName("");
    setSurplusDeficitUploaded(false);
    setUploadStatus("pending");
  };

  const handleUploadError = () => {
    setUploadStatus("error");
  };

  const handleTradeAdded = () => {
    setTradeAdded(true);
  };

  const handleStartSimulation = async () => {
    setLoading(true);
    setError(null);

    const isSurplusSubmitted = uploadStatus === "submitted";

    const simulationData = {
      surplusDeficit: isSurplusSubmitted,
      mcpScenario: selectedScenario,
      traderOffers: tradeAdded,
    };

    try {
      // 🔹 Upload only if not already submitted
      if (!isSurplusSubmitted) {
        if (!parsedData) {
          throw new Error(
            "No file data found to upload. Please upload a file.",
          );
        }
        const resAdd = await AddSurplusDeficitData(parsedData);
        if (!resAdd) return;

        // ✅ Mark as submitted after successful upload
        setUploadStatus("submitted");
      }

      // 🔹 Always run simulation
      const res = await StartSimulationProcurementData({
        ...simulationData,
        surplusDeficit: true, // after upload it's always true
      });

      if (res) {
        navigateToPage("simulation");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Simulation failed";
      setError(errorMessage);
      handleUploadError?.();
      console.error("Simulation operation failed:", error);
    } finally {
      setLoading(false);
    }
  };


  // Check if all requirements are met
  const isSimulationEnabled =
    surplusDeficitUploaded && selectedScenario !== "" && tradeAdded;
const fetchOffers = useCallback (async () => {
    
      const statusPower = await getStatusTraderOffersData();
        if(statusPower){
          const mcp = statusPower?.mcpScenario;
          const surplus = statusPower?.surplusDeficit;
          const trade = statusPower?.traderOffers;
          setUploadStatus(surplus);
          setSelectedScenario(mcp);
          setTradeAdded(trade);
          if (surplus === true) {
            setSurplusDeficitUploaded(true);
            setUploadStatus("submitted");
          }

        }
      
    }
  , []);
  // Load offers on mount
  useEffect(() => {     
    fetchOffers();
  }, [fetchOffers, refreshKey]);

  useEffect(() => {
    if (refreshKey > 0) {
      // setSelectedScenario("");
      // setUploadStatus("pending");
    }
  }, [refreshKey]);
  return (
    <div className="w-full h-full px-3 py-3 !bg-[#F5F7FA]">
      {loading && <Loading />}
      <div className="w-full h-full mx-auto space-y-2">
        <div className="flex lg:flex-nowrap md:flex-wrap gap-4 justify-between w-full">
          {/* Surplus Deficit Section */}

          <UploadBtn
            modalTitle="Surplus Deficit Upload"
            uploadStatus={uploadStatus}
            uploadedFileName={fileName}
            onDataParsed={handleDataParsed}
            onFileDeleted={handleFileDeleted}
            onUploadError={handleUploadError}
          />

          {/* MCP Scenario Section */}
          <div className="bg-[#FFFFFF] rounded-2xl border border-[#CAD5E2] px-2 py-2 w-full">
            <div className="flex items-center justify-between">
              <span className="text-[#6F6D7C] text-sm font-medium">
                Choose Market Price Method
              </span>
              <div className="relative w-100">
                <select
                  value={selectedScenario}
                  onChange={(e) => setSelectedScenario(e.target.value)}
                  className="w-[278px] !h-[40px] py-1 px-4 pr-10 border border-[#A9A8B1] rounded-xl !text-xs text-[#8C8A96] font-medium bg-white appearance-none outline-none cursor-pointer"
                >
                  <option value="">Select Scenario</option>
                  <option value="1">Last Year</option>
                  <option value="2">Last 2 Year Avg</option>
                </select>
                {/* <div className="pointer-events-none absolute inset-y-0 right-[-1px] top-[-3px] flex items-center pr-[10px] text-[#9AA0A6]">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div> */}
              </div>
            </div>
          </div>
        </div>
        {/* Add trader */}
        <div className="w-full h-[calc(100vh-272px)]">
          <PowerAddOffer
            onOfferAdded={handleTradeAdded}
            refreshKey={refreshKey}
            setRefreshKey={setRefreshKey}
          />
          {/* Start Simulation Button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={handleStartSimulation}
              disabled={!isSimulationEnabled}
              className={`px-4 py-2 rounded-xl text-base font-medium border transition-colors shadow-sm ${
                isSimulationEnabled
                  ? "bg-[#F1935C] text-white border-[#F1935C] hover:bg-white hover:text-[#F1935C] cursor-pointer"
                  : "bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed opacity-50"
              }`}
            >
              {loading ? "Loading..." : "Start Simulation"}
            </button>
          </div>
        </div>
      </div>
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </div>
  );
}

export default SurplusDeficitForm;
